// Partner Authentication Provider
// Handles authentication for Sales Partners separately from regular users

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const PartnerAuthContext = createContext(null);

export function usePartnerAuth() {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  }
  return context;
}

// Generate unique referral code
function generateReferralCode(name) {
  const prefix = name
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 4)
    .padEnd(4, 'X');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${random}`;
}

export function PartnerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Skip if we're in the middle of registration (prevents race condition)
      if (isRegistering) {
        return;
      }

      if (firebaseUser) {
        // Check if this user is a sales_partner
        const partnerDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const partnerData = partnerDoc.data();

        if (partnerData?.account_type === 'sales_partner') {
          setUser(firebaseUser);
          setPartner(partnerData);
        } else {
          // Not a partner, clear state
          setUser(null);
          setPartner(null);
        }
      } else {
        setUser(null);
        setPartner(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isRegistering]);

  // Register new sales partner
  async function register({ email, password, displayName, phone, companyName }) {
    const normalizedEmail = email.toLowerCase().trim();

    // Prevent onAuthStateChanged from interfering during registration
    setIsRegistering(true);

    let cred;
    try {
      // Create Firebase Auth user FIRST (this will fail if email already exists in Auth)
      cred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
    } catch (authError) {
      setIsRegistering(false);
      throw authError;
    }

    try {
      // Now we're signed in, check if email already exists in Firestore users collection
      const existingQuery = query(
        collection(db, 'users'),
        where('email', '==', normalizedEmail)
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        // Email exists in Firestore, delete the auth user we just created
        await cred.user.delete();
        throw new Error('An account with this email already exists.');
      }

      // Generate unique referral code
      let referralCode = generateReferralCode(displayName);

      // Ensure referral code is unique
      let codeExists = true;
      let attempts = 0;
      while (codeExists && attempts < 10) {
        const codeQuery = query(
          collection(db, 'users'),
          where('referral_code', '==', referralCode)
        );
        const codeSnap = await getDocs(codeQuery);
        if (codeSnap.empty) {
          codeExists = false;
        } else {
          referralCode = generateReferralCode(displayName);
          attempts++;
        }
      }

      // Create partner document
      const partnerData = {
        uid: cred.user.uid,
        email: normalizedEmail,
        displayName: displayName.trim(),
        phone: phone?.trim() || '',
        company_name: companyName?.trim() || '',
        account_type: 'sales_partner',
        referral_code: referralCode,
        commission_rate: 0.10, // 10% default commission
        status: 'active',
        total_earnings: 0,
        pending_earnings: 0,
        paid_earnings: 0,
        referred_schools: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', cred.user.uid), partnerData);

      // Verify the document was created successfully
      const verifyDoc = await getDoc(doc(db, 'users', cred.user.uid));
      if (!verifyDoc.exists() || verifyDoc.data()?.account_type !== 'sales_partner') {
        throw new Error('Failed to create partner account. Please try again.');
      }

      setUser(cred.user);
      setPartner({ ...partnerData, createdAt: new Date(), updatedAt: new Date() });
      setIsRegistering(false);
      setLoading(false);

      return { user: cred.user, partner: partnerData };
    } catch (error) {
      setIsRegistering(false);
      // If anything fails after creating auth user, clean up
      try {
        await cred.user.delete();
      } catch (deleteError) {
        console.error('Failed to delete auth user after error:', deleteError);
      }
      throw error;
    }
  }

  // Login existing sales partner
  async function login(email, password) {
    const normalizedEmail = email.toLowerCase().trim();
    const cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);

    // Verify this is a sales partner account
    const partnerDoc = await getDoc(doc(db, 'users', cred.user.uid));

    if (!partnerDoc.exists()) {
      await signOut(auth);
      throw new Error('Partner account not found. Please register first.');
    }

    const partnerData = partnerDoc.data();

    if (partnerData?.account_type !== 'sales_partner') {
      await signOut(auth);
      throw new Error(`This account is registered as "${partnerData?.account_type || 'unknown'}". Please use the regular login page for bus companies.`);
    }

    if (partnerData?.status === 'inactive') {
      await signOut(auth);
      throw new Error('Your partner account has been deactivated. Please contact support.');
    }

    setUser(cred.user);
    setPartner(partnerData);

    return { user: cred.user, partner: partnerData };
  }

  // Logout
  async function logout() {
    await signOut(auth);
    setUser(null);
    setPartner(null);
  }

  // Refresh partner data
  async function refreshPartner() {
    if (!user) return;
    const partnerDoc = await getDoc(doc(db, 'users', user.uid));
    setPartner(partnerDoc.data());
  }

  const value = {
    user,
    partner,
    loading,
    register,
    login,
    logout,
    refreshPartner,
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
}

export default PartnerAuthProvider;
